import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useClasses } from './ClassesContext';
import { useEnrollments } from '../enrollments/EnrollmentsProvider';
import classSessionsService from '../../services/classSessionsService';
import api from '../../services/api';
import { 
  ArrowLeft, 
  Edit, 
  Copy, 
  Trash2, 
  Calendar, 
  Star, 
  Users,
  MapPin,
  Clock,
  DollarSign,
  User,
  BookOpen,
  AlertCircle,
  CheckCircle,
  Tag,
  FileText,
  CreditCard,
  Target,
  ClipboardList
} from 'lucide-react';

const ClassView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { classes, getClass, deleteClasses, cloneClass, loading } = useClasses();
  const { enrollments } = useEnrollments();
  const [classData, setClassData] = useState(null);
  const [classLoading, setClassLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [waitlistEntries, setWaitlistEntries] = useState([]);
  const [waitlistLoading, setWaitlistLoading] = useState(false);

  useEffect(() => {
    const loadClass = async () => {
      try {
        setClassLoading(true);
        setError(null);

        console.log('ClassView - Looking for class with ID:', id);

        // Always fetch from API to ensure we have the latest data including waitlistEnabled
        if (getClass) {
          console.log('ClassView - Fetching class from API...');
          const fetchedClass = await getClass(id);
          console.log('ClassView - Fetched class:', fetchedClass);
          console.log('ClassView - waitlistEnabled value:', fetchedClass.waitlistEnabled);
          setClassData(fetchedClass);
        } else {
          throw new Error('Class not found');
        }
      } catch (error) {
        console.error('Error loading class:', error);
        setError(error.message || 'Failed to load class');
      } finally {
        setClassLoading(false);
      }
    };

    if (id) {
      loadClass();
    }
  }, [id, getClass]);

  // Load sessions for this class
  useEffect(() => {
    const loadSessions = async () => {
      if (!classData?.id) return;
      
      try {
        setSessionsLoading(true);
        const result = await classSessionsService.getSessionsForClass(classData.id);
        if (result.success) {
          setSessions(result.data || []);
        }
      } catch (error) {
        console.error('Failed to load sessions for class:', classData.id, error);
      } finally {
        setSessionsLoading(false);
      }
    };

    loadSessions();
  }, [classData?.id]);

  // Load waitlist entries for this class
  useEffect(() => {
    const loadWaitlist = async () => {
      if (!classData?.id) {
        console.log('ClassView - No class ID, skipping waitlist load');
        return;
      }

      console.log('ClassView - Loading waitlist for class:', classData.id);
      console.log('ClassView - Waitlist enabled?', classData.waitlistEnabled, classData.waitlist_enabled);

      try {
        setWaitlistLoading(true);
        const response = await api.request(`/enrollments/waitlist/${classData.id}`);
        console.log('ClassView - Waitlist response:', response);
        setWaitlistEntries(response.waitlist || []);
      } catch (error) {
        console.error('Failed to load waitlist for class:', classData.id, error);
        setWaitlistEntries([]);
      } finally {
        setWaitlistLoading(false);
      }
    };

    loadWaitlist();
  }, [classData?.id]);

  // Get enrollments for this class (excluding cancelled enrollments)
  const classEnrollments = enrollments.filter(enrollment => {
    const matchesClass = enrollment.classId === classData?.id || enrollment.class_id === classData?.id;
    const enrollmentStatus = (enrollment.enrollmentStatus || enrollment.enrollment_status || 'active').toLowerCase();
    const isNotCancelled = enrollmentStatus !== 'cancelled';
    return matchesClass && isNotCancelled;
  });

  // Calculate payment statistics
  const calculatePayments = () => {
    if (!classEnrollments.length) {
      return { total: 0, paid: 0, pending: 0 };
    }

    let total = 0;
    let paid = 0;
    let pending = 0;

    classEnrollments.forEach(enrollment => {
      const paymentStatus = enrollment.paymentStatus || enrollment.payment_status;
      const totalAmount = parseFloat(enrollment.totalAmount || enrollment.total_amount || classData?.price || 0);
      
      // Calculate amount paid based on payment status
      let amountPaid;
      if (paymentStatus === 'paid') {
        // If marked as paid, use the actual amount_paid if available, otherwise assume full amount
        const recordedAmountPaid = parseFloat(enrollment.amountPaid || enrollment.amount_paid || 0);
        amountPaid = recordedAmountPaid > 0 ? recordedAmountPaid : totalAmount;
      } else {
        // For pending/overdue payments, use the recorded amount paid
        amountPaid = parseFloat(enrollment.amountPaid || enrollment.amount_paid || 0);
      }
      
      paid += amountPaid;
      total += totalAmount;
      pending += (totalAmount - amountPaid);
    });

    return { total, paid, pending };
  };

  const payments = calculatePayments();

  const getGenderPlaceholderStyle = (gender) => {
    switch (gender?.toLowerCase()) {
      case 'female':
        return 'bg-gradient-to-br from-pink-300 to-pink-500';
      case 'male':
        return 'bg-gradient-to-br from-blue-300 to-blue-500';
      default:
        return 'bg-gradient-to-br from-gray-300 to-gray-500';
    }
  };

  const handleEdit = () => {
    navigate(`/classes/edit/${classData.id}`);
  };

  const handleClone = async () => {
    try {
      await cloneClass(classData.id);
      console.log(`Successfully cloned class: ${classData.title}`);
      // Optionally navigate back to catalog
      navigate('/classes');
    } catch (error) {
      console.error('Failed to clone class:', error);
    }
  };

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await deleteClasses([classData.id]);
      navigate('/classes');
    } catch (error) {
      console.error('Failed to delete class:', error);
    }
    setShowDeleteModal(false);
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty?.toLowerCase()) {
      case 'beginner': return 'bg-[#B7F1B6] text-[#3C3C3C]';
      case 'intermediate': return 'bg-[#FFECC5] text-[#3C3C3C]';
      case 'advanced': return 'bg-[#FFC6C5] text-[#3C3C3C]';
      default: return 'bg-[#D9D9D9] text-[#3C3C3C]';
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'TBD';
    
    // Handle timezone-safe date parsing
    const utcDate = new Date(dateString);
    const year = utcDate.getUTCFullYear();
    const month = utcDate.getUTCMonth();
    const day = utcDate.getUTCDate();
    
    // Create a local date with the same date components
    const localDate = new Date(year, month, day);
    
    return localDate.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (classLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <div className="w-8 h-8 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin mx-auto mb-4"></div>
            <span className="text-gray-600 dark:text-gray-300">Loading class details...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-red-800 mb-2">Error Loading Class</h2>
            <p className="text-red-700 mb-4">{error}</p>
            <button
              onClick={() => navigate('/classes')}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              Back to Classes
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!classData) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 text-center">
            <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-yellow-800 mb-2">Class Not Found</h2>
            <p className="text-yellow-700 mb-4">The class you're looking for doesn't exist or has been removed.</p>
            <button
              onClick={() => navigate('/classes')}
              className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              Back to Classes
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/classes')}
                className="flex items-center text-gray-600 dark:text-gray-300 hover:text-[#7A99D8] transition-colors"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back to Classes
              </button>
              <div className="h-6 w-px bg-gray-300 dark:bg-gray-600"></div>
              <div className="flex items-center space-x-3">
                <BookOpen className="w-8 h-8 text-[#7A99D8]" />
                <div>
                  <h1 className="text-lg font-bold text-[#3C3C3C] dark:text-white">{classData.title}</h1>
                  <p className="text-sm text-gray-600 dark:text-gray-300">Class Details</p>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleEdit}
                className="bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 px-4 py-2 rounded-lg font-medium flex items-center space-x-2 transition-colors"
              >
                <Edit className="w-4 h-4" />
                <span>Edit</span>
              </button>
              <button
                onClick={handleClone}
                className="bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 px-4 py-2 rounded-lg font-medium flex items-center space-x-2 transition-colors"
              >
                <Copy className="w-4 h-4" />
                <span>Clone</span>
              </button>
              <button
                onClick={handleDelete}
                className="bg-red-100 hover:bg-red-200 text-red-700 px-4 py-2 rounded-lg font-medium flex items-center space-x-2 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                <span>Delete</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Class Information */}
          <div className="lg:col-span-2 space-y-6">
            {/* Class Overview */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-lg font-semibold text-[#3C3C3C] dark:text-white mb-4">Class Overview</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Instructor</label>
                    <div className="flex items-center">
                      <User className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="text-[#3C3C3C] dark:text-white">{classData.instructor}</span>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Category</label>
                    <div className="flex items-center">
                      <Tag className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="text-[#3C3C3C] dark:text-white">{classData.category}</span>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Skill Level</label>
                    <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getDifficultyColor(classData.skillLevel)}`}>
                      {classData.skillLevel}
                    </span>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Price</label>
                    <div className="flex items-center">
                      <DollarSign className="w-4 h-4 text-gray-400 mr-2" />
                      <div>
                        <span className="text-[#3C3C3C] dark:text-white font-semibold">
                          ${(parseFloat(classData.price || 0) + parseFloat(classData.materialsFee || 0)).toFixed(2)}
                        </span>
                        {classData.materialsFee > 0 && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            Base: ${parseFloat(classData.price || 0).toFixed(2)} + Materials: ${parseFloat(classData.materialsFee || 0).toFixed(2)}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Capacity</label>
                    <div className="flex items-center">
                      <Users className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="text-[#3C3C3C] dark:text-white">
                        {classData.currentEnrollment || 0} / {classData.maxCapacity} students
                      </span>
                    </div>
                  </div>
                  
                  {classData.location && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Location</label>
                      <div className="flex items-center">
                        <MapPin className="w-4 h-4 text-gray-400 mr-2" />
                        <span className="text-[#3C3C3C] dark:text-white">{classData.location}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {classData.description && (
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Description</label>
                  <p className="text-[#3C3C3C] dark:text-white">{classData.description}</p>
                </div>
              )}

              {classData.materialsDescription && (
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Materials Included</label>
                  <p className="text-[#3C3C3C] dark:text-white">{classData.materialsDescription}</p>
                </div>
              )}
            </div>

            {/* Schedule Information */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-lg font-semibold text-[#3C3C3C] dark:text-white mb-4">Schedule</h2>
              
              {sessionsLoading ? (
                <div className="text-center py-4">
                  <div className="w-6 h-6 border-2 border-gray-200 border-t-gray-600 rounded-full animate-spin mx-auto mb-2"></div>
                  <span className="text-gray-500">Loading schedule...</span>
                </div>
              ) : sessions.length > 0 ? (
                <div className="space-y-3">
                  {sessions.slice(0, 5).map((session, index) => (
                    <div key={session.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <span className="text-[#3C3C3C] dark:text-white">{formatDate(session.session_date)}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Clock className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-600 dark:text-gray-300">{formatTime(session.start_time)}</span>
                      </div>
                    </div>
                  ))}
                  {sessions.length > 5 && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
                      And {sessions.length - 5} more sessions...
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-gray-500 dark:text-gray-400">No sessions scheduled yet.</p>
              )}
            </div>
          </div>

          {/* Sidebar with Stats and Enrollments */}
          <div className="space-y-6">
            {/* Payment Statistics */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-[#3C3C3C] dark:text-white mb-4">Payment Summary</h3>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600 dark:text-gray-300">Total Expected:</span>
                  <span className="font-semibold text-[#3C3C3C] dark:text-white">${payments.total.toFixed(0)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600 dark:text-gray-300">Amount Paid:</span>
                  <span className="font-semibold text-green-600">${payments.paid.toFixed(0)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600 dark:text-gray-300">Outstanding:</span>
                  <span className="font-semibold text-red-600">${payments.pending.toFixed(0)}</span>
                </div>
                <div className="pt-2 border-t border-gray-200 dark:border-gray-600">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-300">Enrollments:</span>
                    <span className="font-semibold text-[#3C3C3C] dark:text-white">{classEnrollments.length}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Enrolled Students */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-[#3C3C3C] dark:text-white mb-4">
                Enrolled Students ({classEnrollments.length})
              </h3>
              
              {classEnrollments.length > 0 ? (
                <div className="space-y-3">
                  {classEnrollments.map((enrollment) => (
                    <div key={enrollment.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <div className="flex items-center space-x-3">
                        {/* Student Photo/Initials Badge */}
                        <div className="relative w-10 h-10 flex-shrink-0 rounded-lg overflow-hidden">
                          {enrollment.student_photo ? (
                            <img
                              src={enrollment.student_photo}
                              alt={`${enrollment.student_first_name} ${enrollment.student_last_name}`}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div
                            className={`w-full h-full flex items-center justify-center text-white text-sm font-medium ${getGenderPlaceholderStyle(enrollment.gender)}`}
                            style={{ display: enrollment.student_photo ? 'none' : 'flex' }}
                          >
                            {(enrollment.student_first_name || '').charAt(0)}{(enrollment.student_last_name || '').charAt(0)}
                          </div>
                        </div>
                        
                        {/* Student Info */}
                        <div>
                          <div className="font-medium text-[#3C3C3C] dark:text-white">
                            {enrollment.student_first_name} {enrollment.student_last_name}
                          </div>
                          <div className="text-sm text-gray-600 dark:text-gray-300">
                            {enrollment.parent_name}
                          </div>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <div className={`text-xs px-2 py-1 rounded-full ${
                          (enrollment.paymentStatus || enrollment.payment_status) === 'paid' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {(enrollment.paymentStatus || enrollment.payment_status) || 'pending'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 dark:text-gray-400">No students enrolled yet.</p>
              )}
            </div>

            {/* Waitlist Section - Only show if waitlist is enabled */}
            {(() => {
              const shouldShow = classData?.waitlistEnabled || classData?.waitlist_enabled;
              console.log('ClassView - Waitlist section check:', {
                waitlistEnabled: classData?.waitlistEnabled,
                waitlist_enabled: classData?.waitlist_enabled,
                shouldShow,
                classData
              });
              return shouldShow;
            })() && (
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <ClipboardList className="w-5 h-5 text-[#7A99D8]" />
                  <h3 className="text-lg font-semibold text-[#3C3C3C] dark:text-white">
                    Waitlist ({waitlistEntries.length})
                  </h3>
                </div>

                {waitlistLoading ? (
                  <div className="text-center py-4">
                    <div className="w-6 h-6 border-3 border-[#7A99D8] border-t-transparent rounded-full animate-spin mx-auto"></div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">Loading waitlist...</p>
                  </div>
                ) : waitlistEntries.length > 0 ? (
                  <div className="space-y-3">
                    {waitlistEntries.map((entry) => (
                      <div key={entry.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div className="flex items-center space-x-3">
                          {/* Position Badge */}
                          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#7A99D8] text-white flex items-center justify-center text-sm font-semibold">
                            #{entry.position}
                          </div>

                          {/* Student Initials Badge */}
                          <div className="relative w-10 h-10 flex-shrink-0 rounded-lg overflow-hidden">
                            <div className="w-full h-full flex items-center justify-center text-white text-sm font-medium bg-gradient-to-br from-gray-300 to-gray-500">
                              {(entry.student_first_name || '').charAt(0)}{(entry.student_last_name || '').charAt(0)}
                            </div>
                          </div>

                          {/* Student Info */}
                          <div>
                            <div className="font-medium text-[#3C3C3C] dark:text-white">
                              {entry.student_first_name} {entry.student_last_name}
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-300">
                              {entry.parent_email}
                            </div>
                          </div>
                        </div>

                        <div className="text-right">
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            Joined {new Date(entry.waitlist_date).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric'
                            })}
                          </div>
                          <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                            {entry.notified ? (
                              <span className="text-emerald-600 flex items-center gap-1">
                                <CheckCircle className="w-3 h-3" />
                                Notified
                              </span>
                            ) : (
                              <span className="text-gray-400">Not notified</span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 dark:text-gray-400">No one on the waitlist yet.</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <AlertCircle className="w-6 h-6 text-red-500 mr-3" />
                <h3 className="text-lg font-semibold text-[#3C3C3C] dark:text-white">Delete Class</h3>
              </div>
              
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Are you sure you want to delete "{classData.title}"? This action cannot be undone.
              </p>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-white rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors"
                >
                  Delete Class
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClassView;